import type { ConfigNav, ConfigSchema } from "@/types/clinic-configuration";
import {
  agentConfigurationPages,
  clinicGeneralPages,
  clinicOperationPages,
} from "@/lib/schemas";

import { getSchemaSections } from "./configuration-schema-runtime";

/**
 * The anchor a section renders on, from the key it has in its page schema.
 * Namespaced so a section key can never collide with an id the rest of the
 * page owns.
 */
export const configSectionAnchorId = (sectionKey: string) =>
  `config-${sectionKey}`;

/**
 * Key order in a page schema is the order its sections render in, which is
 * the order the rail has to list them — `useScrollSpy` reads the ids as a
 * document-order sequence.
 */
const sectionIndex = (
  { extraAnchors = [] }: ConfigSchema.Page,
  schema: ConfigSchema.Resource
) => {
  const entries: ConfigNav.Entry[] = [
    ...extraAnchors,
    ...getSchemaSections(schema.extensions ?? {}).map(([key, section]) => ({
      id: configSectionAnchorId(key),
      label: section.title ?? key,
    })),
  ];

  /**
   * An index of one entry repeats the page header and says nothing more.
   */
  return entries.length > 1 ? entries : [];
};

/**
 * The rail's full map of one domain: every page, each with its own
 * sections, and the `group` label the rail lists them under.
 *
 * Derived from the page schemas rather than handed over by whichever page
 * is open, so the sections of a page stay listed while another page is
 * being read — the rail is the map of the area, and a map that blanks the
 * part you are not standing on is not one.
 */
export const buildConfigNavPages = (
  pages: { page: ConfigSchema.Page; schema: ConfigSchema.Resource }[],
  group?: string
): ConfigNav.Page[] =>
  pages.map(({ page, schema }) => {
    const { to, label, icon, count } = page;
    return {
      to,
      label,
      icon,
      count,
      group,
      sections: sectionIndex(page, schema),
    };
  });

export const agentConfigurationNavPages = buildConfigNavPages(
  agentConfigurationPages
);

/**
 * Needs for i18next-scanner
 */
// t('General')
// t('Operation')
export const clinicConfigurationNavPages = [
  ...buildConfigNavPages(clinicGeneralPages, "General"),
  ...buildConfigNavPages(clinicOperationPages, "Operation"),
];
